import React, { useState } from 'react';
import {
    ActivityItem, ContextualMenuItemType, DefaultButton, Dropdown, IContextualMenuItem, IDropdownOption, Icon,
    IconButton, Link, Modal, Spinner, SpinnerSize, Stack
} from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetExtensionActivitiesQuery } from '../../store/Api';
import { useLazyResolveRecordUrlQuery } from '../../store/endpoints/RecordLinksEndpoints';
import { IExtensionActivity, IExtensionActivityReference } from '../../models/Extension';
import { EXTENSION_ACTIVITY_TYPE, ExtensionActivityDisplay } from '../../Constants';

/** One thing the user can start from the Run menu. */
export interface IExtensionRunTarget {
    /** Stable key: the sub-extension identifier, or a pseudo-key for folio-level actions like CRM sync. */
    key: string;
    /** Menu label, e.g. "Product Import" or "Won Opportunities". */
    text: string;
    run: () => void;
}

interface IExtensionActivitiesProps {
    /** Folio source id, or the installed-extension id for a standalone extension. */
    context: string;
    /** Folio sub-extensions, used to populate the per–sub-extension filter (omit for standalone). */
    subExtensions?: { identifier: string; name: string }[];
    /**
     * Things the user can start from the Run menu. A folio now has several (product import, won opportunity
     * import, CRM sync), so this is a list rather than the single "Run Import" command it replaced. Omit or pass
     * an empty list to hide the button.
     */
    runTargets?: IExtensionRunTarget[];
    /** Runs every target in {@link runTargets}. Omit to leave "Run all" off the menu. */
    onRunAll?: () => void;
    runDisabled?: boolean;
}

const REFERENCE_TYPE_STOREFRONT_ORDER = 'StorefrontOrder';
const REFERENCE_TYPE_OPPORTUNITY = 'Opportunity';
const REFERENCE_TYPE_SALES_ORDER = 'SalesOrder';

const activityIcon = (activity: IExtensionActivity): string => {
    if (activity.success === false) return 'ErrorBadge';
    switch (activity.activityType) {
        case EXTENSION_ACTIVITY_TYPE.IMPORT_INITIALIZED:
        case EXTENSION_ACTIVITY_TYPE.IMPORT_STARTED:
            return 'CloudDownload';
        case EXTENSION_ACTIVITY_TYPE.IMPORT_CALLBACK_RECEIVED:
            return 'Inbox';
        case EXTENSION_ACTIVITY_TYPE.IMPORT_FINISHED:
            return 'ProductList';
        case EXTENSION_ACTIVITY_TYPE.OPPORTUNITY_CREATED:
        case EXTENSION_ACTIVITY_TYPE.OPPORTUNITY_UPDATED:
            return 'Lightbulb';
        case EXTENSION_ACTIVITY_TYPE.SALES_ORDER_CREATED:
            return 'Receipt';
        case EXTENSION_ACTIVITY_TYPE.SYNC_FINISHED:
            return 'Sync';
        case EXTENSION_ACTIVITY_TYPE.SETTINGS_UPDATED:
            return 'Settings';
        default:
            return 'Info';
    }
};

const changedFieldChips = (fields: string[]): React.ReactNode => (
    <p key='fields' style={{ marginTop: '0.25em', marginBottom: '0.25em', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {fields.map(f => (
            <span key={f} style={{
                background: '#f3f2f1',
                border: '1px solid #d2d0ce',
                borderRadius: '2px',
                padding: '1px 6px',
                fontSize: '12px'
            }}>{f}</span>
        ))}
    </p>
);

const ExtensionActivities: React.FC<IExtensionActivitiesProps> = ({ context, subExtensions, runTargets, onRunAll, runDisabled }) => {
    const [subFilter, setSubFilter] = useState<string | undefined>(undefined);
    const { data, isFetching, refetch } = useGetExtensionActivitiesQuery(
        { context, subExtension: subFilter, count: 100 },
        { skip: !context }
    );

    const subOptions: IDropdownOption[] | undefined = subExtensions && subExtensions.length > 0
        ? [{ key: '', text: 'All sub-extensions' }, ...subExtensions.map(s => ({ key: s.identifier, text: s.name }))]
        : undefined;

    // With a single target the menu is pure friction, so the button just runs it. With several, the primary click
    // runs everything and the menu picks one.
    const runMenuItems: IContextualMenuItem[] = [
        ...(onRunAll && (runTargets?.length ?? 0) > 1
            ? [{ key: '__all', text: 'Run all enabled', iconProps: { iconName: 'PlayerSettings' }, onClick: () => onRunAll() },
               { key: '__divider', itemType: ContextualMenuItemType.Divider }]
            : []),
        ...(runTargets ?? []).map(t => ({ key: t.key, text: t.text, onClick: () => t.run() }))
    ];
    const singleTarget = runTargets?.length === 1 ? runTargets[0] : undefined;

    return (
        <Stack>
            <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.5em' }} style={{ marginBottom: '0.5em' }}>
                {subOptions && (
                    <Dropdown
                        options={subOptions}
                        selectedKey={subFilter ?? ''}
                        onChange={(_e, option) => setSubFilter(option?.key ? String(option.key) : undefined)}
                        styles={{ root: { minWidth: 220 } }}
                        ariaLabel='Filter by sub-extension'
                    />
                )}
                <Stack.Item grow><span /></Stack.Item>
                {(runTargets?.length ?? 0) > 0 && (
                    <DefaultButton
                        text='Run'
                        iconProps={{ iconName: 'Play' }}
                        disabled={runDisabled}
                        onClick={singleTarget ? () => singleTarget.run() : onRunAll}
                        menuProps={singleTarget ? undefined : { items: runMenuItems }}
                    />
                )}
                <IconButton
                    iconProps={{ iconName: 'Refresh' }}
                    title='Refresh'
                    disabled={isFetching}
                    onClick={refetch}
                />
            </Stack>
            {isFetching ? (
                <Spinner
                    label='Loading activity...'
                    size={SpinnerSize.large}
                    style={{ marginTop: '3em' }}
                    styles={{ circle: { width: '60px', height: '60px', borderWidth: '4px' } }}
                />
            ) : !data?.items?.length ? (
                <p style={{ color: '#605e5c', marginTop: '1em' }}>No activity recorded for this extension yet.</p>
            ) : (
                <Stack tokens={{ childrenGap: '1em' }} style={{ marginTop: '0.5em' }}>
                    {data.items.map(activity => (
                        <ExtensionActivityRow key={activity.id} activity={activity} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

const ExtensionActivityRow: React.FC<{ activity: IExtensionActivity }> = ({ activity }) => {
    const history = useHistory();
    const [showDetails, setShowDetails] = useState(false);
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();
    const commentStyle: React.CSSProperties = { marginTop: '0.25em', marginBottom: '0.25em' };
    const failed = activity.success === false;

    // Opportunity/SalesOrder references point at records that live in the activity's folio (a CRM/PSA), not
    // in Adaptive Catalog, so there's no in-app panel to deep-link to. Instead resolve a live deep-link URL
    // from the folio and open it in the CRM, same mechanism as the storefront order panel's "Open in {CRM}".
    const openCrmRecord = async (recordType: string, recordId: string) => {
        if (!activity.context) {
            toast.error('Unable to open record');
            return;
        }
        const crmName = activity.contextName ?? 'the CRM';
        // Resolving the URL calls out to the folio's CRM and can take a moment, so keep a loading toast up
        // until it resolves so the click clearly does something.
        const toastId = toast.loading(`Opening record in ${crmName}…`);
        try {
            const res = await resolveRecordUrl({ folioId: activity.context, recordType, recordId }).unwrap();
            if (!res.supported) {
                toast.update(toastId, { type: 'error', render: `This record can't be opened in ${crmName}`, isLoading: false, autoClose: 4000 });
            } else if (res.success && res.url) {
                window.open(res.url, '_blank', 'noopener');
                toast.dismiss(toastId);
            } else {
                // Supported, but the CRM couldn't resolve the record (e.g. NotFound), surface its actual error.
                toast.update(toastId, { type: 'error', render: res.error?.trim() || `Could not open record in ${crmName}`, isLoading: false, autoClose: 5000 });
            }
        } catch {
            toast.update(toastId, { type: 'error', render: 'Failed to open record', isLoading: false, autoClose: 4000 });
        }
    };

    const renderReference = (ref: IExtensionActivityReference): React.ReactNode => {
        const display = ref.label || ref.referenceId;
        switch (ref.referenceType) {
            case REFERENCE_TYPE_STOREFRONT_ORDER:
                return <Link key={`${ref.referenceType}-${ref.referenceId}`} onClick={() => history.push(`/storefront/orders?orderId=${encodeURIComponent(ref.referenceId)}`)}>Order {display}</Link>;
            case REFERENCE_TYPE_SALES_ORDER:
                return <Link key={`${ref.referenceType}-${ref.referenceId}`} onClick={() => openCrmRecord(ref.referenceType, ref.referenceId)}>Sales Order {display}</Link>;
            case REFERENCE_TYPE_OPPORTUNITY:
                return <Link key={`${ref.referenceType}-${ref.referenceId}`} onClick={() => openCrmRecord(ref.referenceType, ref.referenceId)}>Opportunity {display}</Link>;
            default:
                return <span key={`${ref.referenceType}-${ref.referenceId}`}>{ref.referenceType} {display}</span>;
        }
    };

    const description = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <span>
                {activity.subExtensionName && <><strong>{activity.subExtensionName}</strong>&nbsp;·&nbsp;</>}
                {ExtensionActivityDisplay(activity.activityType)}
                {activity.user && activity.user !== 'SYSTEM' ? <>&nbsp;by {activity.user}</> : ''}
            </span>
            <IconButton
                iconProps={{ iconName: 'Info' }}
                title='View details'
                ariaLabel='View details'
                onClick={() => setShowDetails(true)}
                styles={{
                    root: { height: 24, width: 24, color: failed ? '#a4262c' : '#605e5c' },
                    icon: { fontSize: 13 }
                }}
            />
        </span>
    );

    // Concise inline comments, the full error and metadata live in the details modal.
    const comments: React.ReactNode[] = [];
    if (activity.message) {
        comments.push(<p key='msg' style={commentStyle}>{activity.message}</p>);
    }
    if (activity.activityType === EXTENSION_ACTIVITY_TYPE.IMPORT_FINISHED) {
        comments.push(
            <p key='counts' style={commentStyle}>
                Created {activity.productsCreated ?? 0} · Updated {activity.productsUpdated ?? 0} · Skipped {activity.productsSkipped ?? 0}
                {activity.productsLinkedToMetadata != null && <> · Linked to metadata {activity.productsLinkedToMetadata}</>}
                {activity.productsMetadataRefreshed != null && activity.productsMetadataRefreshed > 0 &&
                    <> · Metadata refreshed {activity.productsMetadataRefreshed}</>}
            </p>
        );
    }
    if (activity.changedFields?.length) {
        comments.push(changedFieldChips(activity.changedFields));
    }
    if (activity.references?.length) {
        comments.push(
            <p key='refs' style={{ ...commentStyle, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {activity.references.map(renderReference)}
            </p>
        );
    }

    return (
        <>
            <ActivityItem
                activityDescription={description}
                comments={comments.length ? comments : undefined}
                timeStamp={new Date(activity.timestamp).toLocaleString()}
                activityIcon={<Icon iconName={activityIcon(activity)} style={failed ? { color: '#a4262c' } : undefined} />}
            />
            <ExtensionActivityDetailsModal
                activity={activity}
                isOpen={showDetails}
                onDismiss={() => setShowDetails(false)}
                renderReference={renderReference}
            />
        </>
    );
};

interface IDetailsModalProps {
    activity: IExtensionActivity;
    isOpen: boolean;
    onDismiss: () => void;
    renderReference: (ref: IExtensionActivityReference) => React.ReactNode;
}

const ExtensionActivityDetailsModal: React.FC<IDetailsModalProps> = ({ activity, isOpen, onDismiss, renderReference }) => {
    const failed = activity.success === false;
    const status = failed ? 'Failed' : activity.success === true ? 'Success' : '-';
    const hasCounts = activity.productsCreated != null || activity.productsUpdated != null || activity.productsSkipped != null
        || activity.productsLinkedToMetadata != null || activity.productsMetadataRefreshed != null;

    const row = (label: string, value: React.ReactNode) => (
        <div style={{ display: 'flex', padding: '5px 0', borderBottom: '1px solid #f3f2f1' }}>
            <div style={{ width: 150, color: '#605e5c', flexShrink: 0 }}>{label}</div>
            <div style={{ flexGrow: 1, wordBreak: 'break-word' }}>{value}</div>
        </div>
    );

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss} isBlocking={false}>
            <div style={{ padding: '1.5em', minWidth: 480, maxWidth: 680 }}>
                <Stack horizontal verticalAlign='center' horizontalAlign='space-between'>
                    <h2 style={{ margin: 0, fontSize: 18 }}>{ExtensionActivityDisplay(activity.activityType)}</h2>
                    <IconButton iconProps={{ iconName: 'Cancel' }} ariaLabel='Close' onClick={onDismiss} />
                </Stack>
                <div style={{ marginTop: '1em' }}>
                    {row('Status', <span style={{ color: failed ? '#a4262c' : undefined }}>{status}</span>)}
                    {row('Timestamp', new Date(activity.timestamp).toLocaleString())}
                    {activity.user && row('User', activity.user)}
                    {activity.subExtensionName && row('Sub-extension', activity.subExtensionName)}
                    {activity.message && row('Message', activity.message)}
                    {activity.changedFields?.length ? row('Changed Settings', changedFieldChips(activity.changedFields)) : null}
                    {hasCounts && row('Created / Updated / Skipped',
                        `${activity.productsCreated ?? 0} / ${activity.productsUpdated ?? 0} / ${activity.productsSkipped ?? 0}`)}
                    {activity.productsLinkedToMetadata != null && row('Linked to metadata', String(activity.productsLinkedToMetadata))}
                    {activity.productsMetadataRefreshed != null && activity.productsMetadataRefreshed > 0 &&
                        row('Metadata refreshed', String(activity.productsMetadataRefreshed))}
                    {activity.recordCount != null && row('Records', String(activity.recordCount))}
                    {activity.durationSeconds != null && row('Duration', `${activity.durationSeconds.toFixed(1)}s`)}
                    {activity.taskId && row('Task ID', <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{activity.taskId}</span>)}
                    {activity.context && row('Context', <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{activity.context}</span>)}
                    {activity.references?.length ? row('References',
                        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{activity.references.map(renderReference)}</span>) : null}
                </div>
                {activity.error && (
                    <div style={{ marginTop: '1.25em' }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Error</div>
                        <pre style={{
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fdf3f4',
                            border: '1px solid #f3d6d8', color: '#a4262c', padding: '0.75em',
                            borderRadius: 2, maxHeight: 320, overflow: 'auto', fontSize: 12, margin: 0
                        }}>{activity.error}</pre>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default ExtensionActivities;
